import { formatSocialOfferForApi, sendFeedbackToData, usePostSocialApply } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { IChallenge } from "@bbj-types/challenge";
import type { ISocialOffer } from "@bbj-types/offer";
import { ProposalType, SocialApplyStatus } from "@bbj-types/socialApply/enum";
import { useGlobalContext } from "@root/context";
import { useChallengesByTags } from "@styleguide";
import { isAxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import type { IFollowUpSocialOfferProps } from "./index";

export const SocialApplyStatusToLabel = {
  [SocialApplyStatus.PROPOSED]: "Proposée au candidat",
  [SocialApplyStatus.INTERESTED_CANDIDATE]: "Candidat intéressé",
  [SocialApplyStatus.PRESCRIPTION_FILLED]: "Prescription réalisée",
  [SocialApplyStatus.INTEGRATED_CANDIDATE]: "Candidat intégré",
  [SocialApplyStatus.REFUSED_STRUCTURE]: "Refus par la structure",
  [SocialApplyStatus.REFUSED_CANDIDATE]: "Refus par le candidat",
};

const useSocialOfferPropose = (candidate: ICandidate) => {
  const { user } = useGlobalContext();
  const [{ loading }, postSocialApply] = usePostSocialApply();

  const proposeOffer = async (socialOffer: ISocialOffer, challenges: IChallenge[], status: SocialApplyStatus) => {
    try {
      const socialApply = await postSocialApply({
        candidate: candidate._id,
        organisation: user.organisation._id,
        source: socialOffer.source,
        socialOffer: formatSocialOfferForApi(socialOffer),
        challenges: challenges.map((challenge) => challenge._id),
        status,
        proposal: {
          type: ProposalType.MANUAL,
        },
      });

      void sendFeedbackToData({
        candidate: candidate._id,
        event: "proposed",
        kind: "social",
        recommendations: [socialOffer.offerId],
      });

      if (socialApply.status === 200) {
        window.publish("feedback", {
          severity: "success",
          message: `L’offre est indiquée comme "${SocialApplyStatusToLabel[status]}" et une action a bien été créée`,
        });
        return socialApply;
      }
    } catch (error) {
      window.publish("feedback", {
        severity: "error",
        message: isAxiosError(error)
          ? error.message
          : "Une erreur est survenue lors de la proposition de l'offre. Veuillez contacter un administrateur.",
      });
    }
  };

  return { loading, proposeOffer };
};

export const useFollowUpSocialOffer = <T extends ISocialOffer>(
  offer: IFollowUpSocialOfferProps<T>["offer"],
  onUpdateOffer: IFollowUpSocialOfferProps<T>["onUpdateOffer"],
  candidate: ICandidate
) => {
  const { challenges } = useGlobalContext();
  const { proposeOffer, loading: loadingPropose } = useSocialOfferPropose(candidate);
  const matchingChallenges = useChallengesByTags(challenges, offer.tags);

  const handleProposeSocialOffer = useCallback(
    async (status: SocialApplyStatus, selectedChallenges: IChallenge[]) => {
      if (onUpdateOffer && selectedChallenges.length > 0) {
        const res = await proposeOffer(offer as ISocialOffer, selectedChallenges, status);

        if (res?.status === 200) {
          onUpdateOffer(
            {
              ...offer,
              status: res.data.socialApply[0].status,
              transitions: res.data.socialApply[0].transitions,
              socialApplyId: res.data.socialApply[0]._id,
            },
            res.data.action
          );
        }
      }
    },
    [offer, onUpdateOffer, proposeOffer]
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const candidateAgreedCommunication = useMemo(
    () => candidate.optInEmail || candidate.optInPhone,
    [candidate.optInEmail, candidate.optInPhone]
  );

  return {
    matchingChallenges,
    handleProposeSocialOffer,
    anchorEl,
    setAnchorEl,
    loadingPropose,
    candidateAgreedCommunication,
  };
};
